import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import SlideMenu from './SlideMenu';
import { supabase } from "@/lib/supabase";
import { useLocation, useNavigate } from "react-router-dom";
import axios from 'axios';

const UserAvatar = ({ user, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [cachedImageData, setCachedImageData] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const retryCountRef = useRef(0);
  const loadingRef = useRef(false); // Prevent duplicate requests
  const MAX_RETRIES = 2;

  const getInitials = (name, email) => {
    if (!name && email) {
      return email[0].toUpperCase();
    }
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Check if cached data is still valid (30 days)
  const isCacheValid = (cacheTimestamp) => {
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    return Date.now() - cacheTimestamp < thirtyDaysMs;
  };

  // Try to load image with exponential backoff retry logic
  useEffect(() => {
    if (!user?.picture) return;

    const cacheKey = `avatar_cache_${user.id || user.email}`;
    const cacheTimeKey = `avatar_cache_time_${user.id || user.email}`;
    const cached = localStorage.getItem(cacheKey);
    const cacheTime = localStorage.getItem(cacheTimeKey);

    // Check if cache exists and is still valid
    if (cached && cacheTime && isCacheValid(parseInt(cacheTime))) {
      setCachedImageData(cached);
      setImageUrl(cached);
      setImageError(false);
    } else {
      // Clear expired cache
      if (cached) {
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(cacheTimeKey);
      }
      setImageUrl(user.picture);
    }
    retryCountRef.current = 0;
  }, [user?.picture, user?.id, user?.email]);

  // Download and cache image data as base64 with optimized headers
  // NOTE: Skip download for Google images due to CORS restrictions
  const downloadAndCacheImage = async (url) => {
    if (!url || imageError) return;
    
    // Skip download for Google images - use direct URL instead
    if (url.includes('googleusercontent.com') || url.includes('google.com')) {
      setImageUrl(url);
      return;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'image/*',
          'Accept-Encoding': 'gzip, deflate, br',
          'Cache-Control': 'public, max-age=31536000',
        },
        cache: 'force-cache',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64 = reader.result;
        const cacheKey = `avatar_cache_${user.id || user.email}`;
        const cacheTimeKey = `avatar_cache_time_${user.id || user.email}`;

        try {
          localStorage.setItem(cacheKey, base64);
          localStorage.setItem(cacheTimeKey, Date.now().toString());
          setCachedImageData(base64);
          setImageUrl(base64);
          retryCountRef.current = 0; // Reset retry counter on success
        } catch (e) {
          console.debug('Could not cache image:', e.message);
          setImageUrl(url);
        }
      };

      reader.readAsDataURL(blob);
    } catch (error) {
      console.warn('Failed to download and cache image:', error.message);
      // Only retry on network errors, not on 429
      if (retryCountRef.current < MAX_RETRIES && !error.message.includes('429')) {
        retryCountRef.current += 1;
        const delay = Math.pow(2, retryCountRef.current) * 1000; // Exponential backoff: 2s, 4s
        setTimeout(() => downloadAndCacheImage(url), delay);
      } else if (retryCountRef.current >= MAX_RETRIES) {
        // Max retries exceeded - fallback to initials
        console.warn('Max retries exceeded for avatar image');
        setImageError(true);
      }
    }
  };

  // Handle successful image load
  const handleImageLoad = () => {
    // If we haven't cached it yet and it's a Google URL, try to cache it
    if (user?.picture && !cachedImageData && user.picture.includes('lh3.googleusercontent.com')) {
      // Throttle: only proceed if not already loading
      if (!loadingRef.current) {
        loadingRef.current = true;
        downloadAndCacheImage(user.picture).finally(() => {
          loadingRef.current = false;
        });
      }
    }
  };

  const navigate = useNavigate();

  const handleClick = () => {
    setIsMenuOpen(true);
  };

  // Logout direttamente dall'avatar
const handleLogoutClick = async () => {
  setIsMenuOpen(false);

  try {
    // Clear Supabase session — signOut can deadlock on Capacitor Preferences,
    // so give it 3s max before proceeding with local cleanup.
    const result = await Promise.race([
      supabase.auth.signOut(),
      new Promise(r => setTimeout(r, 3000)),
    ]);
    if (result?.error) console.error("Errore signOut Supabase:", result.error.message);
  } catch (error) {
    console.error("Errore logout:", error.message);
  }

  // Clear all local storage and axios defaults
  localStorage.removeItem('betterhusbandUserId');
  localStorage.removeItem('betterhusbandUser');
  localStorage.removeItem('betterhusbandUserName');
  localStorage.removeItem('betterhusbandGooglePicture');
  localStorage.removeItem('betterhusbandGoglePicture');
  localStorage.setItem('betterhusbandExplicitLogout', '1');
  delete axios.defaults.headers.common.Authorization;
  delete axios.defaults.headers.common['X-User-Id'];
  delete axios.defaults.headers.common['x-user-id'];

  // Call parent logout handler if provided
  if (onLogout) onLogout();

  navigate("/login", { replace: true });
};
  return (
    <>
      <div className="relative inline-block">
        <button
          onClick={handleClick}
          data-testid="user-avatar-btn"
          className="w-8 h-8 bg-inherit hover:bg-stone-200 flex items-center justify-center text-stone-600 font-semibold text-sm hover:shadow-lg transition-all active:scale-95"
          title={user?.name || 'Menu'}
        >
          <Icon icon="solar:hamburger-menu-linear" width={20} height={20} />
        </button>
      </div>

      {/* SlideMenu opzionale */}
      <SlideMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        user={user}
        onLogout={handleLogoutClick} // chiama logout dall'avatar
      />
    </>
  );
};

export default UserAvatar;
